import { create } from 'zustand';

/**
 * Read workspace ID from URL (used for initialization and fallback)
 */
function getWorkspaceIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return params.get('ws');
}

interface WorkspaceState {
  activeWorkspaceId: string | null;
  setActiveWorkspaceId: (id: string | null) => void;
}

export const useWorkspaceStore = create<WorkspaceState>((set) => ({
  // Initialize from URL synchronously on store creation
  activeWorkspaceId: getWorkspaceIdFromUrl(),
  setActiveWorkspaceId: (id) => {
    set({ activeWorkspaceId: id });
    // Sync to URL for bookmark/reload convenience
    syncWorkspaceToUrl(id);
  },
}));

/**
 * Sync workspace ID to URL without triggering navigation
 * Updates ?ws= param for bookmarking and page reload
 */
function syncWorkspaceToUrl(workspaceId: string | null) {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);

  if (workspaceId) {
    url.searchParams.set('ws', workspaceId);
  } else {
    url.searchParams.delete('ws');
  }

  // Update URL without reload or navigation
  window.history.replaceState({}, '', url.toString());
}

/**
 * Get active workspace ID (for use outside React components)
 * Used by API client interceptors
 * Falls back to URL if store not yet set (handles race conditions)
 */
export function getActiveWorkspaceId(): string | null {
  const storeValue = useWorkspaceStore.getState().activeWorkspaceId;
  return storeValue || getWorkspaceIdFromUrl();
}
